import { z } from 'zod';
import type { DamAncestry } from '../../models/DamAncestry';
import { useQueryClient } from '@tanstack/react-query';
import AncestryApi from '../../apis/ancestry_api';
import { damAncestorKeys } from '../../queries/AlpacaQueries';
import { handleError } from '../../apis';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import RHFInput from '../RHF/RHFInput';
import { RHFSubmitButton } from '../RHF/RHFSubmitButton';

const validator_damAncestry = z.object({
  damAncestryId: z.number().optional(),
  applicationUserId: z.string(),
  alpacaId: z.number(),
  dam: z.string().trim().nullish(),
  sireParent1: z.string().trim().nullish(),
  damParent1: z.string().trim().nullish(),
  sireParent2: z.string().trim().nullish(),
  damParent2: z.string().trim().nullish(),
  sireParent3: z.string().trim().nullish(),
  damParent3: z.string().trim().nullish(),
  sireParent4: z.string().trim().nullish(),
  damParent4: z.string().trim().nullish(),
  sireParent5: z.string().trim().nullish(),
  damParent5: z.string().trim().nullish(),
});

type DamAncestryForm = z.infer<typeof validator_damAncestry>;

export default function UpdateDamAncestryDisplay({
  damAncestry,
  close,
}: {
  damAncestry: DamAncestry;
  close: () => void;
}) {
  const queryClient = useQueryClient();

  const update = async (values: DamAncestryForm) => {
    try {
      if (values.damAncestryId && values.damAncestryId > 0) {
        await AncestryApi.updateDamAncestry(values?.alpacaId ?? 0, values);
      } else {
        await AncestryApi.createDamAncestry(values.alpacaId, values);
      }
      queryClient.invalidateQueries({
        queryKey: damAncestorKeys.for(damAncestry.alpacaId),
      });
      close();
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(`Error updating: ${message}`);
    }
  };
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<DamAncestryForm>({
    defaultValues: damAncestry,
    resolver: zodResolver(validator_damAncestry),
  });
  return (
    <div className='sire-ancestry'>
      <form onSubmit={handleSubmit(update)}>
        <div className='update-ancestry-display'>
          <div className='sire-dam ms-2'>
            <RHFInput
              name='dam'
              label='Dam'
              type='text'
              register={register('dam')}
              errors={errors}
            />
          </div>
          <div className='second-generation-sire flex flex-col'>
            <RHFInput
              label='2nd Generation Sire'
              type='text'
              name='sireParent1'
              register={register('sireParent1')}
              errors={errors}
            />
          </div>
          <div className='second-generation-dam flex flex-col'>
            <RHFInput
              label='2nd Generation Dam'
              type='text'
              name='damParent1'
              register={register('damParent1')}
              errors={errors}
            />
          </div>
          <div className='third-generation-sire-sire flex flex-col'>
            <RHFInput
              label='3rd Generation Sire'
              type='text'
              name='sireParent2'
              register={register('sireParent2')}
              errors={errors}
            />
          </div>
          <div className='third-generation-dam-sire flex flex-col'>
            <RHFInput
              label='3rd Generation Dam'
              type='text'
              name='damParent2'
              register={register('damParent2')}
              errors={errors}
            />
          </div>
          <div className='third-generation-sire-dam flex flex-col'>
            <RHFInput
              label='3rd Generation Sire'
              type='text'
              name='sireParent3'
              register={register('sireParent3')}
              errors={errors}
            />
          </div>
          <div className='third-generation-dam-dam flex flex-col'>
            <RHFInput
              label='3rd Generation Dam'
              type='text'
              name='damParent3'
              register={register('damParent3')}
              errors={errors}
            />
          </div>
          <div className='sire-submit'>
            <RHFSubmitButton label='Submit' isSubmitting={isSubmitting} />
          </div>
        </div>
      </form>
    </div>
  );
}
